import { Typography } from "@/frontend/shared/ui/Typography";
import styles from "./TableBody.module.scss";
import { MouseEvent, useState } from "react";
import { DotsThreeIcon, RenameIcon, TrashIcon } from "@/frontend/shared/assets/icons";
import { SquareButton } from "@/frontend/shared/ui/Button";
import { DropshadowMenu } from "@/frontend/shared/ui/DropshadowMenu";
import { ConfirmationModal } from "@/frontend/shared/ui/ConfirmationModal/ConfirmationModal";
import { getFileUrl } from "../../model/services/getFileUrl";
import { deleteFile } from "../../model/services/deleteFile";
import { snackbarConstructor } from "@/frontend/shared/ui/CustomNotifications/ui/Snackbar/snackbarConstructor";
import { RenameModal } from "@/frontend/components/RenameModal/RenameModal";
import { patchRenameFile } from "../../model/services/patchRenameFile";
import { formatDateMonthYear } from "@/frontend/shared/lib/formatDateMonthYear/formatDateMonthYear";
import { EAccessDataStatus } from "@/frontend/features/Company/model/types/types";

export type TDataRoomRow = {
  filename: string;
  id: number;
  createdAt: string;
  createdBy: {
    firstName: string;
    lastName: string;
    profileImageUrl: string;
    organization: {
      name: string;
      id: number;
    };
  };
  parsedData: {
    title: string;
    description: string;
  };
};

export function TableBody({
  data,
  triggerRefetch,
  companyId,
  dataStatusFromAccess,
  companyDataId,
  isLoadingRequestAccess,
  setIsLoadingRequestAccess,
  isDataRoomPending
}: {
  data: TDataRoomRow[];
  triggerRefetch: () => void;
  companyId: number;
  dataStatusFromAccess?: EAccessDataStatus;
  companyDataId?: number;
  isLoadingRequestAccess: boolean;
  setIsLoadingRequestAccess: (value: boolean) => void;
  isDataRoomPending?: boolean;
}) {
  const [shownDropshadow, setShownDropshadow] = useState(-1);
  const [isOpenDeleteFileContainer, setIsOpenDeleteFileContainer] = useState({ id: -1, isOpen: false });
  const [isOpenRenameFileContainer, setIsOpenRenameFileContainer] = useState({ id: -1, isOpen: false });
  const [renameModalValue, setRenameModalValue] = useState("");

  function getFileExtension(filename: string): string {
    return filename.split(".").pop() || "";
  }

  async function onDeleteFile() {
    setIsOpenDeleteFileContainer({ id: -1, isOpen: false });
    try {
      await deleteFile({ id: companyId, fileId: isOpenDeleteFileContainer.id });
      triggerRefetch();
    } catch (error) {
      snackbarConstructor("Error deleting file", "error");
      throw new Error();
    }
  }

  async function onRenameFile() {
    setIsOpenRenameFileContainer({ id: -1, isOpen: false });

    try {
      await patchRenameFile({ fileId: isOpenRenameFileContainer.id, id: companyId, title: renameModalValue });
      triggerRefetch();
    } catch (error) {
      snackbarConstructor("Error renaming file", "error");
      throw new Error();
    } finally {
      setRenameModalValue("");
    }
  }

  async function downloadAndNavigate(fileId: number) {
    try {
      const url = await getFileUrl({ companyId, fileId });
      if (!url) return;

      const link = document.createElement("a");
      link.href = url;
      link.target = "_blank";
      link.setAttribute("download", url);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error("error: ", error);
    }
  }

  function onCloseModal() {
    setIsOpenRenameFileContainer({ id: -1, isOpen: false });
    setRenameModalValue("");
  }

  function onOpenModal(e: MouseEvent<HTMLDivElement>, item: TDataRoomRow) {
    e.stopPropagation();
    setIsOpenDeleteFileContainer({ id: item.id, isOpen: true });
  }

  function onOpenRenameModal(e: MouseEvent<HTMLDivElement>, item: TDataRoomRow) {
    setRenameModalValue(item?.parsedData.title || item?.filename || "");
    e.stopPropagation();
    setIsOpenRenameFileContainer({ id: item.id, isOpen: true });
  }

  return (
    <div className={styles.container}>
      {data?.map((item, index) => (
        <div key={index} className={styles.row} onClick={() => downloadAndNavigate(item.id)}>
          <div className={styles.labelContainer}>
            <div className={styles.fileFormatContainer}>
              <p className={styles.fileFormat}>{getFileExtension(item.filename).toUpperCase()}</p>
            </div>
            <div className={styles.fileContainer}>
              <Typography variant="label-4" className={styles.fileInfoText} color="greyscale900">
                {item.parsedData?.title || ""}
              </Typography>
              <div className={styles.fileInfoContainer}>
                <Typography variant="body-3" color="greyscale600" className={styles.fileInfoText}>
                  {item.filename}
                </Typography>
              </div>
            </div>
          </div>
          <div className={styles.labelContainer}>
            {item.createdBy.profileImageUrl ? (
              <img src={item.createdBy.profileImageUrl} alt="user" className={styles.userImg} />
            ) : (
              <div className={styles.userImg}>
                <div className={styles.userImgIndicator} />
              </div>
            )}
            <div className={styles.userContainer}>
              <Typography variant="label-4" color="greyscale900">
                {item.createdBy.firstName} {item.createdBy.lastName}
              </Typography>
              <Typography variant="body-3" color="greyscale600">
                {item?.createdBy?.organization?.name || "-"}
              </Typography>
            </div>
          </div>
          <div className={styles.labelContainer}>
            <Typography variant="body-2" color="greyscale900">
              {formatDateMonthYear(item.createdAt)}
            </Typography>
          </div>
          <div className={styles.labelContainer}>
            <Typography variant="body-2" color="greyscale900">
              {item.parsedData?.description || "-"}
            </Typography>
          </div>

          <div className={styles.actionsLabelContainer}>
            <SquareButton
              size="small"
              variant="secondary"
              onClick={(e) => {
                e.stopPropagation();
                setShownDropshadow(index);
              }}
            >
              <DotsThreeIcon className={styles.threeDotsIcon} />
            </SquareButton>
            <DropshadowMenu
              isOpen={shownDropshadow === index}
              onChange={() => setShownDropshadow(index)}
              onClose={() => setShownDropshadow(-1)}
              className={styles.optionsContainerClassName}
              options={[
                <div className={styles.dropshadowOption} onClick={(e) => onOpenRenameModal(e, item)}>
                  <RenameIcon />
                  <Typography variant="label-4" color="greyscale800">
                    Rename
                  </Typography>
                </div>,
                <div className={styles.dropshadowOption} onClick={(e) => onOpenModal(e, item)}>
                  <TrashIcon />
                  <Typography variant="label-4" color="greyscale800">
                    Delete
                  </Typography>
                </div>
              ]}
              top="-10px"
              right="0px"
              dividerIndexRate={1}
            />
          </div>
        </div>
      ))}
      <ConfirmationModal
        isOpen={isOpenDeleteFileContainer.isOpen}
        onClose={() => setIsOpenDeleteFileContainer({ id: -1, isOpen: false })}
        onConfirm={() => onDeleteFile()}
        title="Delete file"
        content="Are you sure you want to delete this file?"
      />
      <RenameModal
        isOpen={isOpenRenameFileContainer.isOpen}
        onClose={onCloseModal}
        onSuccess={onRenameFile}
        value={renameModalValue}
        setValue={setRenameModalValue}
        title="Rename file"
        placeholder="Enter new filename"
      />
    </div>
  );
}
